// @flow
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Thunk } from 'app/common/redux/types';
import {
  BOARD_ACTIVITY_OBJECT_TYPES,
  RUD_RESOURCE_MAP,
  type ActivityItemType,
  type BoardActivityItemType,
} from 'app/common/redux/reducers/activityItems';

export type ActivityUpdateData = {
  tags: Array<Object>, // TODO(yansun): better define this
  text: string,
};

// TODO(jwardle): Update data can be partial. Type checking should reflect this with maybe types.
export type ActivityItemUpdated = {
  type: 'ACTIVITY_ITEM_UPDATED',
  payload: {
    data: Object,
  },
};

const REACTION_RESOURCE_MAP = {
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]: 'BoardActivityReactionResource',
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]: 'BoardActivityCommentReactionResource',
};

export function activityItemUpdated(updatedItem: { id: string }): ActivityItemUpdated {
  return {
    type: 'ACTIVITY_ITEM_UPDATED',
    payload: { data: updatedItem },
  };
}

export type ValidReactionTypes =
  | PCONST.thrift.ReactionType.LIKE
  | PCONST.thrift.ReactionType.CLAP
  | PCONST.thrift.ReactionType.LIGHTBULB
  | PCONST.thrift.ReactionType.THINKING
  | PCONST.thrift.ReactionType.THUMBS_DOWN;

function reactionCountUpdated(
  id: string,
  increment: boolean,
  reactionType: ValidReactionTypes = PCONST.thrift.ReactionType.LIKE,
) {
  return {
    type: 'ACTIVITY_ITEM_UPDATE_REACTION_COUNT',
    payload: { id, increment, reactionType },
  };
}

export function updateActivityItem(
  id: string,
  type: ActivityItemType,
  updatedData: ActivityUpdateData,
): Thunk<Promise<any>> {
  return (dispatch) => {
    const resourceName = RUD_RESOURCE_MAP[type];
    const resourceOptions = {
      activityId: id,
      ...updatedData,
    };
    return ResourceFactory.create(resourceName, resourceOptions)
      .callUpdate()
      .then((response: any) => {
        const responseData = response.resource_response.data;
        dispatch(activityItemUpdated(responseData));
        return responseData;
      });
  };
}

export function updateActivityItemReaction(
  id: string,
  type: BoardActivityItemType,
  oldReactionType: $Values<PCONST.thrift.ReactionType>,
  newReactionType: $Values<PCONST.thrift.ReactionType>,
): Thunk<void> {
  return (dispatch) => {
    if (oldReactionType === newReactionType) {
      return;
    }

    const resourceName = REACTION_RESOURCE_MAP[type];
    const resourceOptions = { activityId: id, reactionType: newReactionType };
    const resource = ResourceFactory.create(resourceName, resourceOptions);

    if (newReactionType === PCONST.thrift.ReactionType.NONE) {
      resource.callDelete({ showError: false });
    } else {
      resource.callUpdate({ showError: false });
    }

    if (oldReactionType !== PCONST.thrift.ReactionType.NONE) {
      dispatch(reactionCountUpdated(id, false, oldReactionType));
    }
    if (newReactionType !== PCONST.thrift.ReactionType.NONE) {
      dispatch(reactionCountUpdated(id, true, newReactionType));
    }
  };
}

// Try to use updateActivityItemReaction.
export function toggleActivityItemReaction(
  id: string,
  reactionType: ValidReactionTypes,
  type: BoardActivityItemType,
  isLiked: boolean,
  reactionByMe?: number = PCONST.thrift.ReactionType.NONE,
): Thunk<void> {
  return (dispatch) => {
    const resourceName = REACTION_RESOURCE_MAP[type];

    if (!isLiked && reactionByMe > PCONST.thrift.ReactionType.NONE) {
      dispatch(reactionCountUpdated(id, false, reactionByMe));
      dispatch(reactionCountUpdated(id, true, reactionType));

      const deleteResourceOptions = { activityId: id, reactionType: reactionByMe };
      const deleteResource = ResourceFactory.create(resourceName, deleteResourceOptions);
      const createResourceOptions = { activityId: id, reactionType };
      const createResource = ResourceFactory.create(resourceName, createResourceOptions);

      deleteResource.callDelete({ showError: false }).then(() => {
        createResource.callCreate({ showError: false });
      });
    } else {
      const resourceOptions = { activityId: id, reactionType };
      const resource = ResourceFactory.create(resourceName, resourceOptions);
      isLiked
        ? resource.callDelete({ showError: false })
        : resource.callCreate({ showError: false });
      dispatch(reactionCountUpdated(id, !isLiked, reactionType));
    }
  };
}

export default undefined;
